/**
 * Pure payload helper functions extracted from TraversalStore.
 * These helpers compute which paths to remove, build DAC patches,
 * and clean traversal data before API calls — without depending on
 * MobX state directly.
 */
import { remove } from 'dot-object'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { Data } from 'interfaces/ApolloResponse.interface'
import { Debtor } from 'containers/PersonalForm/PersonalForm.interface'
import { FieldChange } from './types'

/**
 * Returns the list of traversal paths that should be excluded from PUT
 * payloads based on current data state and tenant context.
 */
export const getRemovablePaths = (data: Data, isSmava: boolean): string[] => {
  const paths: string[] = isSmava ? ['common.vehicle.initialPayment'] : []

  if (!data.debtors.primary.currentEmployment.isTemporary) {
    paths.push('debtors.primary.currentEmployment.until')
  }
  if (!data.debtors.secondary.currentEmployment.isTemporary) {
    paths.push('debtors.secondary.currentEmployment.until')
  }

  return paths
}

/**
 * Returns a deep clone of the traversal data with the specified paths
 * removed. Does not mutate the original data object.
 */
export const removePathsFromData = (data: Data, paths: string[]): Data => {
  const dataClone: Data = JSON.parse(JSON.stringify(data))
  paths.forEach((deletePath) => {
    remove(deletePath, dataClone)
  })
  return dataClone
}

/**
 * Computes the full set of paths that should be stripped from the PUT payload,
 * consolidating the logic previously split between `removablePathsFromPayload`
 * and the `put` action in TraversalStore.
 */
export const buildPutPayloadPaths = (
  data: Data,
  isSmava: boolean
): string[] => {
  const paths = getRemovablePaths(data, isSmava)

  if (!data.debtors.primary.hasPreviousEmployment) {
    paths.push('debtors.primary.previousEmployment')
  }
  if (!data.debtors.secondary.hasPreviousEmployment) {
    paths.push('debtors.secondary.previousEmployment')
  }

  return paths
}

/**
 * Returns whether `common.amountAdaption` should be reset to `nothing`
 * based on the current active loans state and page context.
 */
export const shouldResetAmountAdaption = (
  data: Data,
  currentPageInSession: string | null | undefined
): boolean => {
  if (currentPageInSession !== 'activeLoans') return false

  const primaryOk =
    data.debtors.primary.numberOfActiveLoans === 0 ||
    !data.debtors.primary.activeLoans?.some((l) => l.isDebtConversionWanted)

  const secondaryOk =
    data.debtors.secondary.numberOfActiveLoans === 0 ||
    !data.debtors.secondary.activeLoans?.some((l) => l.isDebtConversionWanted)

  return primaryOk && secondaryOk
}

/**
 * Builds the list of FieldChange patches needed to record a DAC
 * (Digital Account Check) transaction against the correct debtor(s).
 * Returns an empty array and fires an Amplitude event when the
 * transactionId is falsy.
 */
export const buildDacTransactionPatches = (
  dacTransactionId: string,
  payoutAccountHolder: string
): FieldChange<string | number>[] => {
  if (!dacTransactionId) {
    trackInAmplitude(AmplitudeEvent.OnDacTranscationalIdNull)
    return []
  }

  const timeStamp = Math.round(new Date().getTime() / 1000)
  const payoutAccountHolderLowerCase = payoutAccountHolder.toLowerCase()

  if (
    payoutAccountHolderLowerCase === Debtor.PRIMARY ||
    payoutAccountHolderLowerCase === Debtor.SECONDARY
  ) {
    return [
      {
        path: `debtors.${payoutAccountHolderLowerCase}.digitalAccountCheck.transactionId`,
        value: dacTransactionId
      },
      {
        path: `debtors.${payoutAccountHolderLowerCase}.digitalAccountCheck.createdAt`,
        value: timeStamp
      }
    ]
  }

  return [
    {
      path: 'debtors.primary.digitalAccountCheck.transactionId',
      value: dacTransactionId
    },
    {
      path: 'debtors.primary.digitalAccountCheck.createdAt',
      value: timeStamp
    },
    {
      path: 'debtors.secondary.digitalAccountCheck.transactionId',
      value: dacTransactionId
    },
    {
      path: 'debtors.secondary.digitalAccountCheck.createdAt',
      value: timeStamp
    }
  ]
}
